export interface BlogPost {
  slug: string;
  title: string;
  author: string;
  date: string;
  excerpt: string;
  content: string;
}

const BLOG_POSTS_4: BlogPost[] = [
  {
    slug: 'why-every-elementary-school-needs-a-student-newspaper',
    title: 'Why Every Elementary School Needs a Student Newspaper',
    author: 'Pressto',
    date: '2026-04-01',
    excerpt:
      'Student newspapers aren\'t just for high school. Here\'s why launching one in elementary school builds stronger writers, critical thinkers, and engaged citizens from day one.',
    content: `Student newspapers have long been a staple of high school hallways. But what if we told you the best time to start a school newspaper isn't in 9th grade — it's in 2nd?

Elementary school is where students form their relationship with writing. For many kids, writing feels like a chore — something they do because they have to, not because they want to. A student newspaper flips that script entirely.

## Writing with purpose

When students write for a newspaper, they're not writing for a grade. They're writing for an audience — their classmates, their teachers, their families. That shift in purpose changes everything. Suddenly, word choice matters. Organization matters. Voice matters. Not because the rubric says so, but because real people are going to read it.

Research consistently shows that authentic writing tasks — those with real audiences and real purposes — produce stronger writing outcomes than traditional prompts. A student newspaper is the most authentic writing task a school can offer.

## Building critical thinking early

Journalism requires students to ask questions, evaluate sources, distinguish fact from opinion, and present information clearly. These aren't just writing skills — they're the foundation of critical thinking.

When a 3rd grader interviews the school librarian about the new reading program, they're practicing skills that will serve them through college and beyond. When a 4th grader writes a review of the school lunch menu, they're learning to support opinions with evidence.

## Creating school community

There's something magical about a school newspaper. It gives students a shared experience to talk about. It highlights achievements that might otherwise go unnoticed. It gives every student a voice — not just the loudest ones.

Schools that launch student newspapers consistently report stronger school culture and higher student engagement. Parents love them. Principals love them. And most importantly, students love them.

## Getting started is easier than you think

The biggest barrier to starting a school newspaper has always been logistics. Who has time to teach journalism on top of everything else? How do you layout a newspaper? How do you print and distribute it?

Modern tools like Pressto have eliminated these barriers. Students can write, edit, and publish a professional-looking newspaper entirely digitally. Teachers can integrate newspaper writing into existing ELA blocks. And distribution is as simple as sharing a link or QR code.

The question isn't whether your school can afford to start a newspaper. It's whether you can afford not to.`,
  },
  {
    slug: 'student-journalism-and-the-science-of-reading',
    title: 'Student Journalism and the Science of Reading: A Perfect Match',
    author: 'Pressto',
    date: '2026-03-25',
    excerpt:
      'The Science of Reading has transformed how we teach reading. But what about writing? Here\'s how student journalism aligns with evidence-based literacy instruction.',
    content: `The Science of Reading movement has rightfully transformed reading instruction across the country. Structured literacy, phonics-based approaches, and evidence-based curricula are replacing outdated methods. But there's a critical piece of the literacy puzzle that often gets overlooked: writing.

Reading and writing are deeply connected. The research is clear — students who write more become better readers, and students who read more become better writers. Yet in many classrooms, writing instruction takes a back seat to reading. Student journalism can change that.

## The reading-writing connection

Decades of research from scholars like Steve Graham and Timothy Shanahan demonstrate that reading and writing share cognitive processes. When students write, they're reinforcing the same skills they use when reading: phonemic awareness, vocabulary, text structure, and comprehension.

A student newspaper leverages this connection naturally. Students read mentor texts (real newspaper articles) to understand structure. They write their own articles, applying what they've learned. They edit and revise, deepening their understanding of language conventions.

## Vocabulary in context

One of the strongest findings in literacy research is that vocabulary is best learned in context, not from word lists. Journalism is a vocabulary-rich activity by nature. Students encounter and use words like "headline," "source," "evidence," "quote," "editorial," and "deadline" — all in meaningful contexts.

When a student writes a news article about the school garden, they're using science vocabulary. When they cover a community event, they're using social studies vocabulary. The newspaper becomes a vehicle for cross-curricular vocabulary development.

## Text structure and comprehension

Understanding text structure is a key predictor of reading comprehension. Journalism teaches text structure explicitly and repeatedly. Students learn the inverted pyramid (most important information first), the difference between news articles and opinion pieces, and how to organize information logically.

These aren't abstract lessons — they're practical skills that students apply immediately. And because newspaper writing has clear, consistent structures, even struggling writers can find success.

## Motivation and engagement

The Science of Reading tells us that knowledge and motivation are critical components of comprehension. Student journalism builds both. Students develop background knowledge on every topic they cover. And the authentic audience of a school newspaper provides intrinsic motivation that no worksheet can match.

When we align writing instruction with evidence-based literacy practices, student journalism isn't just a fun activity — it's a research-backed instructional strategy.`,
  },
  {
    slug: 'writing-blocks-and-the-journalism-process',
    title: 'How Writing Blocks Transform the Student Journalism Process',
    author: 'Pressto',
    date: '2026-03-18',
    excerpt:
      'Great writing isn\'t linear — it\'s recursive. Here\'s how Pressto\'s Writing Blocks help young journalists plan, draft, and revise like real reporters.',
    content: `Professor Jeanne Paratore's research at Boston University makes one thing clear: quality writing develops through iterative stages — planning, drafting, revising, and editing — not in a straight line from start to finish. This is especially true in journalism, where a story evolves as new information emerges.

Yet most writing tools treat composition as linear. Start at the top, write to the bottom, turn it in. That's not how real writers work. And it's not how student journalists should work either.

## The recursive nature of journalism

A journalist rarely writes a story from beginning to end in one pass. They might start with a quote from an interview, then build context around it. They might draft a lead, realize it's burying the real story, and restructure entirely. They gather new information that changes the angle. The story takes shape through iteration.

This is exactly the recursive writing process that research says produces stronger writers. And it's exactly what Pressto's Writing Blocks are designed to support.

## What are Writing Blocks?

Writing Blocks are flexible, modular writing spaces that correspond to the structural components of a text. For a news article, blocks might include: Lead, Background, Quotes, Details, and Conclusion. For an opinion piece: Claim, Evidence, Counterargument, and Call to Action.

Students can add, delete, rearrange, and revise blocks at any point. This modularity mirrors how professional journalists actually compose — working on pieces of a story and assembling them into a coherent whole.

## Structure that enables agency

There's a common misconception that providing structure limits creativity. The research says the opposite. When students understand text structure — the difference between narrative and expository organization, between a news article and an editorial — they make more deliberate choices as writers.

Paratore's research identifies two universal organizational frameworks: narrative structures (setting, characters, problem, resolution) and expository structures (description, problem/solution, comparison, sequence, cause/effect). Journalism uses both, and Writing Blocks make these structures visible and manipulable.

A student writing a feature article can see the structure of their piece laid out in blocks. They can move the quote block before the background block. They can add a new evidence block. They're not just filling in a template — they're making authorial decisions within a research-backed framework.

## Feedback at every stage

Because Writing Blocks break composition into visible stages, teachers can provide feedback throughout the process — not just on the final product. This aligns with what Paratore calls "teaching feedback" versus "auditing feedback." Instead of evaluating a finished article, teachers can step in during planning and say, "Your lead buries the most interesting detail. What if you started with the quote instead?"

Pressto's Writing Buddy provides this kind of in-context feedback automatically, while teachers use the real-time view to monitor progress and intervene with targeted small-group instruction.

## From blocks to published newspaper

The magic of Writing Blocks in a journalism context is that the same tool students use to draft becomes the tool they use to publish. Blocks flow naturally into newspaper layout. The planning structure becomes the article structure becomes the published piece. Writing, revision, and publication are one continuous process.

That's not just good technology design. It's good pedagogy.`,
  },
  {
    slug: 'opinion-writing-through-journalism',
    title: 'Teaching Opinion Writing Through Student Journalism',
    author: 'Pressto',
    date: '2026-03-11',
    excerpt:
      'Opinion writing is one of the hardest genres to teach. Student journalism makes it easier by giving students real topics they care about and a real audience to persuade.',
    content: `Ask any elementary teacher what the hardest writing genre to teach is, and most will say opinion writing. Students struggle to form clear claims, support them with evidence, and address counterarguments. Traditional prompts like "Should students have homework?" feel stale and disconnected.

Student journalism solves this problem by grounding opinion writing in authentic contexts.

## From prompts to editorials

In a traditional classroom, opinion writing starts with a prompt. In a newsroom, it starts with a conversation. "What should change at our school?" "Was the book fair worth it?" "Should recess be longer?"

When students write editorials for their school newspaper, the stakes are real. Their classmates will read their arguments. Their principal might actually respond. This authentic context transforms opinion writing from an exercise into an act of civic participation.

## The editorial structure

Newspaper editorials follow a clear structure that maps directly to standards-based opinion writing:

1. **Hook** — Grab the reader's attention with a surprising fact or bold statement
2. **Claim** — State your opinion clearly
3. **Evidence** — Support your claim with reasons and facts
4. **Counterargument** — Acknowledge the other side
5. **Call to action** — Tell readers what they should do

This structure gives students a framework without being formulaic. Every editorial is different because every topic is different, but the bones are the same.

## Evidence gathering is journalism

One of the biggest challenges in opinion writing is getting students to use evidence instead of just stating preferences. Journalism naturally solves this. When a student wants to argue that the school needs a new water fountain, they don't just say "because the old one is gross." They interview classmates. They count how many students bring water bottles. They research how much water kids should drink daily.

The journalism process — research, interview, investigate — is the evidence-gathering process that opinion writing requires.

## Letters to the editor

Here's a bonus: once your newspaper publishes editorials, other students can write letters to the editor in response. Now you have authentic argumentative discourse happening between students — the kind of critical thinking we dream about but rarely achieve with traditional prompts.

A student newspaper doesn't just teach opinion writing. It creates a culture of thoughtful, evidence-based discourse. And it starts as early as 2nd grade.`,
  },
  {
    slug: 'cross-curricular-journalism-beyond-ela',
    title: 'Beyond ELA: How Student Journalism Fits Into Every Subject',
    author: 'Pressto',
    date: '2026-03-04',
    excerpt:
      'Student journalism isn\'t just for English class. Science reports, math features, social studies investigations — here\'s how newspapers make writing happen across the curriculum.',
    content: `When teachers hear "school newspaper," they think ELA. But the best school newspapers cover everything — and in doing so, they make writing instruction a whole-school initiative rather than a single-subject burden.

## Science: Lab reports become articles

A traditional lab report asks students to document a procedure and record results. A science news article asks students to explain why it matters. "Our class tested which paper airplane design flies farthest. Here's what we discovered — and why it surprised us."

Same content, completely different writing task. The article requires students to understand the science well enough to explain it to someone who wasn't there. That's deeper learning.

## Social Studies: Investigations and features

Social studies is natural journalism territory. Students can write feature articles about historical events tied to current lessons. "What was life like in our town 100 years ago?" They can interview community members, visit local landmarks, and write reported pieces that connect curriculum to their own world.

When 4th graders study state history, they can write travel features about interesting places in their state. When 5th graders study the American Revolution, they can write newspaper articles as if they were colonial reporters. The possibilities are endless.

## Math: Data journalism

Data journalism is one of the fastest-growing fields in professional newsrooms. Elementary students can do it too. "We surveyed 100 students about their favorite school lunch. Here are the results."

Students collect data, create charts and graphs, and write about what the numbers mean. They're doing math and writing simultaneously — and neither feels like a chore because there's a real purpose behind it.

## Specials: Arts and PE coverage

Don't forget the specials teachers. The art show, the spring concert, field day, the visiting author — these are all newsworthy events that students can cover. This gives specials teachers a way to connect to literacy goals without changing what they teach.

It also gives those teachers and programs visibility in the school community. When a student writes a glowing review of the spring concert, the music teacher gets well-deserved recognition.

## Making it work

The key to cross-curricular journalism is coordination, not extra time. Instead of replacing existing writing assignments, transform them. The persuasive essay becomes an editorial. The research report becomes a feature article. The book report becomes a review.

Students write the same amount. They hit the same standards. But the output is a newspaper that the whole school community reads and celebrates. That's the power of journalism across the curriculum.`,
  },
  {
    slug: 'student-voice-and-sel-through-journalism',
    title: 'How School Newspapers Build Student Voice and Social-Emotional Skills',
    author: 'Pressto',
    date: '2026-02-25',
    excerpt:
      'Student journalism isn\'t just about writing skills. It builds confidence, empathy, collaboration, and a sense of belonging — the core of social-emotional learning.',
    content: `In the rush to improve test scores and meet standards, it's easy to overlook the social-emotional dimension of learning. But SEL isn't separate from academics — it's the foundation. And student journalism is one of the most powerful vehicles for building social-emotional skills while simultaneously advancing academic goals.

## Voice and agency

Many students — especially younger ones, quieter ones, and those from marginalized communities — go through school feeling like their perspectives don't matter. A school newspaper changes that. When a student's byline appears on an article that the whole school reads, the message is clear: your voice matters.

This isn't abstract. Students who participate in school journalism programs report higher levels of school belonging and self-efficacy. They see themselves as contributors to their community, not just consumers of information.

## Empathy through interviewing

Interviewing is a core journalism skill, and it's also a masterclass in empathy. When a student interviews the school custodian about their daily routine, they're practicing active listening, asking follow-up questions, and seeing the world through someone else's eyes.

These are SEL competencies — social awareness, relationship skills — practiced in an authentic context. No role-playing exercise can match the empathy-building power of a real conversation with a real person.

## Collaboration and responsibility

Publishing a newspaper is a team effort. Students depend on each other to meet deadlines, provide feedback, and produce quality work. When one student doesn't finish their article, the whole newspaper is affected.

This kind of authentic collaboration — where the stakes are real and the work is visible — builds responsibility and teamwork in ways that group projects often don't.

## Handling feedback

Student journalists learn to receive and give constructive feedback. An editor might say, "This paragraph is confusing — can you explain what you mean?" That's a hard thing to hear, but in the context of making the newspaper better, it feels different than red marks on an essay.

Over time, students develop resilience around feedback. They learn that revision isn't punishment — it's how good writing gets made. That's a mindset shift that serves them well beyond the classroom.

## Celebrating every student

The beauty of a school newspaper is that it has room for everyone. The strong writer can pen the lead story. The artist can create illustrations. The data lover can make charts. The social butterfly can conduct interviews. The quiet observer can write a thoughtful reflection.

Every student can find their role in a newsroom. And when every student has a role, every student belongs.`,
  },
  {
    slug: 'journalism-for-ells-and-multilingual-learners',
    title: 'Why Student Journalism Works for English Language Learners',
    author: 'Pressto',
    date: '2026-02-18',
    excerpt:
      'For multilingual learners, writing can feel intimidating. Student journalism lowers the barrier by providing structure, scaffolds, and a motivating reason to write.',
    content: `English Language Learners face unique challenges in writing instruction. They're simultaneously learning a new language and learning to write in it. Traditional writing assignments — open-ended prompts with minimal structure — can feel overwhelming. Student journalism offers a better path.

## Structure reduces cognitive load

Journalism has built-in structures that support ELLs. A news article follows the inverted pyramid. An interview has a predictable format. A headline is short and direct. These structures give multilingual learners a framework to hang their ideas on, reducing the cognitive load of figuring out both what to say and how to organize it.

For an ELL student, "Write about your weekend" is paralyzing. "Write a 5-sentence news article about the school garden using the 5 W's" is achievable. The structure doesn't limit creativity — it enables it.

## Sentence frames become journalism tools

Teachers of ELLs already use sentence frames extensively. Journalism provides natural sentence frames that don't feel remedial:

- "According to [source], ..."
- "[Person] said, '[quote].'"
- "The event took place at [location] on [date]."
- "When asked about [topic], [person] explained that ..."

These are professional writing patterns used by real journalists. ELL students learn them as journalism skills, not as language accommodations. That distinction matters for confidence.

## Interviews build oral language

For ELLs, oral language development is critical and often precedes writing development. Journalism naturally integrates speaking and listening. Students prepare interview questions (writing), conduct interviews (speaking and listening), and write up what they learned (writing again).

This cycle of listening, speaking, and writing mirrors best practices in language acquisition. And because interviews are purposeful conversations — not drills — they build communicative competence in authentic ways.

## Cultural assets become stories

Every multilingual learner brings cultural knowledge and lived experiences that enrich a school newspaper. A student who recently immigrated can write about their home country. A bilingual student can translate articles for families who speak other languages. A student navigating two cultures can write a perspective piece that helps classmates understand their experience.

Journalism reframes what ELLs bring to the classroom as assets, not deficits. Their unique perspectives aren't obstacles to overcome — they're stories worth telling.

## Visual supports in newspaper design

Newspaper layout naturally incorporates visual elements — photos, illustrations, charts, and captions. For ELLs, these visual supports aid comprehension both as readers and writers. A student who struggles to write a long article can write a photo caption or create an infographic. Every contribution matters, and every contribution builds language skills.

Student journalism meets ELLs where they are and gives them a reason to stretch. That combination of support and motivation is exactly what multilingual learners need.`,
  },
  {
    slug: 'principal-guide-to-school-newspapers',
    title: 'A Principal\'s Guide to Supporting a School Newspaper Program',
    author: 'Pressto',
    date: '2026-02-11',
    excerpt:
      'Thinking about bringing a student newspaper to your school? Here\'s what principals need to know about supporting, sustaining, and showcasing a journalism program.',
    content: `As a principal, you're always looking for initiatives that check multiple boxes: academic rigor, student engagement, school culture, and parent communication. A school newspaper checks all of them.

But launching and sustaining a newspaper program requires some thought. Here's what you need to know.

## Start with a champion

Every successful school newspaper has a teacher champion — someone who's excited about the idea and willing to lead it. This doesn't need to be an ELA teacher. Some of the best school newspaper advisors are social studies teachers, librarians, or instructional coaches.

Your job as principal is to find that champion and remove barriers for them. Give them time, resources, and encouragement. The rest will follow.

## Integrate, don't add

The number one reason school newspaper programs fail is that they're treated as an add-on. If it's an after-school club that depends on volunteer time, it will fade when life gets busy.

Instead, encourage your champion to integrate newspaper writing into existing instructional time. Opinion writing unit? Students write editorials. Research project? Students write feature articles. The newspaper becomes the output of work that's already happening.

## Make it visible

When your students publish a newspaper, celebrate it. Share it in your weekly parent newsletter. Post it on the school website. Display printed copies in the front office. Read student articles during morning announcements.

Visibility does two things: it motivates students (they see that their work matters) and it builds community support (parents and community members see what your school is doing).

## Use it for family engagement

School newspapers are a surprisingly powerful family engagement tool. When a child comes home and says "I wrote an article — here's the link," parents engage. They read it. They share it with grandparents. They talk about it at dinner.

This is organic family engagement that doesn't require planning a family night or sending home a flyer. The newspaper does the work for you.

## Budget considerations

A digital school newspaper costs far less than most school initiatives. With a tool like Pressto, you're looking at around $999/year for a school-wide license — less than a single assembly speaker or one set of classroom novels.

And unlike a one-time assembly, a newspaper program produces visible results every month, all year long.

## Measuring impact

How do you know if your newspaper program is working? Look for:

- **Writing quality**: Compare student writing samples from the beginning and end of the year
- **Engagement**: Track how many students are contributing and how many families are reading
- **Culture**: Survey students about school belonging and voice
- **Cross-curricular connections**: Note how many subjects are represented in each issue

A school newspaper is one of the rare initiatives that improves academics, builds culture, and engages families — all at once. The investment is small. The return is enormous.`,
  },
  {
    slug: 'ai-as-writing-partner-not-writing-replacement',
    title: 'AI as Writing Partner, Not Writing Replacement: What the Research Says',
    author: 'Pressto',
    date: '2026-02-04',
    excerpt:
      'The fear that AI will write for students misses the point. When used correctly, AI is a drafting partner that gives teachers back time for the instruction only humans can provide.',
    content: `The conversation around AI in education has been dominated by fear. Will students use ChatGPT to cheat? Will AI replace the need to learn writing? These concerns are understandable. They're also missing the bigger picture.

As Professor Jeanne Paratore, Boston University's Professor Emerita of Literacy Education and Pressto's Chief Education Advisor, puts it: "AI is unlikely to write for us, but rather, might best be viewed as a tool to write with us."

That distinction — writing *with* versus writing *for* — changes everything.

## The time problem

Here's the reality teachers face: research recommends at least one hour of daily writing instruction starting in first grade — 30 minutes of explicit instruction plus 30 minutes of practice. On top of that, teachers need substantial time outside school hours to read and respond to student drafts.

This is where writing instruction breaks down. Not because teachers don't know what good instruction looks like, but because there literally aren't enough hours in the day. Providing meaningful, individualized feedback on 25 or 30 student drafts is a monumental task.

## AI reclaims teacher time

This is where AI shines — not as a replacement for the teacher, but as a force multiplier. AI can handle the time-consuming tasks that currently prevent teachers from doing their best work:

**Prompt generation.** Instead of spending evenings crafting writing assignments, teachers can leverage AI to generate cognitively challenging, relevant, accessible topics aligned with their current curriculum.

**Structural guidance.** AI can provide graphic organizers and planning frameworks aligned with research-based text structures, giving students the scaffolding they need before they start drafting.

**In-process feedback.** This is the big one. Pressto's Writing Buddy provides real-time hints based on the specific assignment and student progress — suggesting signal words, prompting for missing elements, and offering topically relevant vocabulary. Students get feedback immediately, not three days later when they've already moved on.

## What AI can't do

AI can't build relationships with students. It can't read a student's face and know they're struggling. It can't lead a small-group conference where three students who share a common challenge work through it together with expert guidance. It can't celebrate a breakthrough with genuine warmth.

These are the things that make great writing teachers irreplaceable. And these are exactly the things teachers should be spending their time on — not marking commas on 30 papers at 10 PM.

## AI in the journalism classroom

Student journalism is an ideal context for AI as writing partner. When a student is drafting a news article, the Writing Buddy might notice they haven't included a direct quote and suggest: "Strong news articles include quotes from people involved. Who could you interview about this?" That's not writing for the student. That's coaching — the same thing a teacher would say if they had time to sit with every student individually.

Meanwhile, the teacher uses Pressto's real-time view to monitor all students' progress simultaneously. They spot three students who are struggling with their leads and pull them for a quick small-group lesson. That's targeted instruction informed by data — the kind of teaching that moves the needle.

## The path forward

The question isn't whether AI belongs in writing instruction. It's how we use it. When AI handles the logistical burden of feedback and scaffolding, teachers are free to do what they do best: teach. And students get the responsive, timely support they need to grow as writers.

That's not a threat to education. That's a breakthrough.`,
  },
  {
    slug: 'from-5ws-to-front-page-journalism-across-grade-levels',
    title: 'From the 5 W\'s to the Front Page: Journalism Across Grade Levels',
    author: 'Pressto',
    date: '2026-01-28',
    excerpt:
      'Student journalism looks different in 2nd grade than it does in 5th. Here\'s how to scale the complexity while keeping every age group engaged and learning.',
    content: `One of the most common questions we hear from teachers is: "My students are too young for journalism." We respectfully disagree. Student journalism works at every elementary grade level. It just looks different.

The key is matching the complexity of the journalism task to the developmental stage of the writer. Here's how it works across K-5.

## Grades K-1: Classroom news reporters

The youngest students can absolutely participate in journalism — they just do it with more support. In kindergarten and first grade, journalism looks like:

- **Class news board**: Students dictate or draw "news" from their lives (new baby sibling, lost tooth, family trip) and the teacher scribes it
- **Shared writing**: The whole class collaborates on a single news story, with the teacher modeling the writing process on a projected screen
- **Photo journalism**: Students take photos around the school and write (or dictate) one-sentence captions

At this level, the goal isn't a polished newspaper. It's building the foundational understanding that writing communicates information to an audience. That's purpose and audience awareness — two of Paratore's core principles — starting in kindergarten.

## Grades 2-3: Guided journalism

By second and third grade, students can write their own short articles with structured support. This is where Pressto's Writing Blocks shine. A 2nd grader might work with three blocks:

1. **What happened?** (The 5 W's)
2. **What did someone say about it?** (A quote)
3. **Why does it matter?** (A closing thought)

That's a complete news article structure, simplified for emerging writers. The blocks provide the scaffold; the student provides the content.

At this level, pair students for interviews. Second graders asking each other "What's your favorite thing about our school?" is journalism. It's also oral language development, active listening, and social-emotional learning.

## Grade 3-4: Independent reporting

Third and fourth graders are ready for more independence. They can:

- Choose their own story topics from an editorial brainstorm
- Conduct interviews with adults (teachers, staff, parents)
- Write articles with 4-5 paragraphs using the inverted pyramid structure
- Peer edit each other's work using simple checklists

This is also where opinion writing enters the picture. Fourth graders have strong opinions about everything — recess length, homework, cafeteria food. Channel that energy into editorials with a claim-evidence-counterargument structure.

## Grade 5: The full newsroom

Fifth graders can run a nearly independent newsroom. They can:

- Hold editorial meetings and pitch story ideas
- Assign roles (editor-in-chief, section editors, reporters, photographers)
- Write multiple article types (news, features, opinion, reviews, interviews)
- Design newspaper layouts
- Manage publication deadlines

At this level, the teacher shifts from instructor to advisor — still guiding, still teaching, but letting students drive the process. This is Paratore's principle of agentive learning in action: students making choices about topics, formats, and approaches within a structured framework.

## The thread that connects all levels

Regardless of grade level, student journalism does the same thing: it gives students a real purpose for writing and a real audience to write for. A kindergartner drawing a picture of the class pet for the "news board" and a 5th grader investigating why the water fountain is always broken are both doing journalism. The complexity scales. The motivation stays constant.

That's why starting early matters. By the time students reach middle school, they don't need to be taught that writing matters. They already know. They've been journalists for years.`,
  },
];

export default BLOG_POSTS_4;
